import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, MapPin, Menu, Phone } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { MobileNav } from "@/components/site/MobileNav";
import { SERVICES } from "@/data/services";
import { KOELN, LOCATIONS, UMLAND } from "@/data/areas";
import { ServiceIcon } from "@/lib/icons";
import { SITE } from "@/lib/site";
import { cn } from "@/lib/utils";

const navLink = ({ isActive }) =>
  cn(
    "rounded-full px-4 py-2 text-[15px] font-semibold text-brand-anthracite transition-colors hover:bg-white/50",
    isActive && "bg-brand-anthracite text-white hover:bg-brand-anthracite",
  );

const NavDropdown = ({ label, testId, active, panelClass, children }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-trigger`}
        className={navLink({ isActive: active }) + " inline-flex items-center gap-1"}
      >
        {label} <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className={cn("absolute top-full z-50 pt-3", panelClass)} data-testid={`${testId}-menu`}>
          <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-lift">{children}</div>
        </div>
      )}
    </div>
  );
};

const ServicesMenu = () => {
  const { pathname } = useLocation();
  return (
    <NavDropdown label="Leistungen" testId="nav-services" active={SERVICES.some((s) => pathname === `/${s.slug}`)} panelClass="left-0 w-[340px]">
      {SERVICES.map((s) => (
        <Link
          key={s.slug}
          to={`/${s.slug}`}
          data-testid={`nav-service-${s.slug}`}
          className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-semibold text-brand-anthracite transition-colors hover:bg-brand-sky-light hover:text-brand-blue"
        >
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-mist text-brand-blue">
            <ServiceIcon name={s.icon} className="h-5 w-5" />
          </span>
          {s.name}
        </Link>
      ))}
    </NavDropdown>
  );
};

const areaLink = "block rounded-lg px-3 py-1.5 text-[14.5px] font-semibold text-brand-anthracite transition-colors hover:bg-brand-sky-light hover:text-brand-blue";

const AreasMenu = () => {
  const { pathname } = useLocation();
  const active = pathname === "/einsatzgebiet" || LOCATIONS.some((l) => pathname === `/${l.slug}`);
  return (
    <NavDropdown label="Einsatzgebiet" testId="nav-areas" active={active} panelClass="left-1/2 w-[520px] -translate-x-1/2">
      <div className="grid grid-cols-2 gap-2 p-2">
        {[{ title: "Köln", items: KOELN }, { title: "Umland", items: UMLAND }].map((g) => (
          <div key={g.title}>
            <p className="px-3 pb-1 text-xs font-bold uppercase tracking-wider text-brand-blue">{g.title}</p>
            {g.items.map((l) => (
              <Link key={l.slug} to={`/${l.slug}`} className={areaLink} data-testid={`nav-area-${l.slug}`}>{l.label}</Link>
            ))}
          </div>
        ))}
      </div>
      <Link to="/einsatzgebiet" className="mt-1 flex items-center justify-between rounded-xl bg-brand-mist px-4 py-2.5 text-sm font-bold text-brand-blue hover:bg-brand-sky-light" data-testid="nav-areas-overview">
        Gesamtes Einsatzgebiet ansehen <MapPin className="h-4 w-4" />
      </Link>
    </NavDropdown>
  );
};

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-brand-yellow shadow-[0_1px_0_rgba(0,0,0,0.08)]" data-testid="site-header">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Hauptnavigation">
          <ServicesMenu />
          <AreasMenu />
          <NavLink to="/faq" className={navLink} data-testid="nav-faq">FAQ</NavLink>
          <NavLink to="/kontakt" className={navLink} data-testid="nav-contact">Kontakt</NavLink>
        </nav>
        <div className="flex items-center gap-2">
          <a
            href={SITE.phoneHref}
            data-testid="header-call-button"
            className="hidden items-center gap-2 rounded-full bg-brand-blue px-5 py-2.5 text-[15px] font-bold text-white shadow-soft transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-brand-blue-dark sm:inline-flex"
          >
            <Phone className="h-4 w-4" /> 24h: {SITE.phone}
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Menü öffnen"
            className="grid h-11 w-11 place-items-center rounded-xl bg-brand-anthracite text-white lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
      <MobileNav open={mobileOpen} onOpenChange={setMobileOpen} />
    </header>
  );
};
